(() => {
  if (window.czDraperyHardware) return;

  const canonicalHeader = value => {
    const header = (value || '').trim();
    if (/^double pleats?(?:\s|\(|$)/i.test(header)) return 'Pleated';
    if (/^triple pleats?(?:\s|\(|$)/i.test(header)) return 'Pleated';
    if (/^goblet(?:\s|\(|$)/i.test(header)) return 'Pleated';
    if (/^pleated(?:\s|\(|$)/i.test(header)) return 'Pleated';
    if (/^grommet(?:s)?(?:\s|\(|$)/i.test(header)) return 'Grommet';
    if (/^soft top(?:\s|\(|$)/i.test(header)) return 'Soft Top';
    if (/^ripple fold(?:\s|\(|$)/i.test(header)) return 'Ripple Fold';
    return header;
  };

  const fitsHeader = (card, header) => (card.dataset.czHeaderFits || '')
    .split('|')
    .map(canonicalHeader)
    .filter(Boolean)
    .includes(canonicalHeader(header));

  function init(section, options = {}) {
    if (!section) return null;
    if (section.czHardwareController) return section.czHardwareController;
    const track = section.querySelector('.cz-hardware-track');
    const controls = section.querySelector('.cz-hardware-controls');
    const empty = section.querySelector('.cz-hardware-empty');
    if (!track || !controls || !empty) return null;

    const baseCards = [...track.querySelectorAll('.cz-discovery-card')];
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const pause = controls.querySelector('[data-cz-hardware-pause]');
    let cards = [];
    let clones = [];
    let lastHeader = null;
    let manualPaused = false;
    let hovered = false;
    let focused = false;
    let visible = false;
    let renderGeneration = 0;
    let wrapTimer = 0;

    const step = () => cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
    const jump = left => {
      const old = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      track.style.scrollBehavior = old;
    };
    const current = () => {
      const distance = step();
      return distance ? Math.min(cards.length, Math.round(track.scrollLeft / distance)) : 0;
    };
    const createClone = card => {
      const clone = card.cloneNode(true);
      clone.dataset.czHardwareClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(node => { node.tabIndex = -1; });
      return clone;
    };

    const render = header => {
      const activeElementWasInTrack = track.contains(document.activeElement);
      const activeElementWasInControls = controls.contains(document.activeElement);
      renderGeneration += 1;
      if (wrapTimer) {
        clearTimeout(wrapTimer);
        wrapTimer = 0;
      }
      cards = header ? baseCards.filter(card => fitsHeader(card, header)) : [...baseCards];
      clones = cards.length > 1 ? cards.map(createClone) : [];
      track.replaceChildren(...cards, ...clones);
      track.hidden = cards.length === 0;
      empty.hidden = cards.length !== 0;
      controls.hidden = cards.length < 2;
      jump(0);
      section.dataset.czSelectedHeader = header;
      section.dataset.czHardwareMatchCount = String(cards.length);
      section.dataset.czHardwareState = cards.length ? (cards.length === 1 ? 'single' : 'multiple') : 'empty';
      if ((activeElementWasInTrack && !track.contains(document.activeElement)) || (activeElementWasInControls && controls.hidden)) {
        (cards.length ? track : empty).focus({preventScroll: true});
      }
      return cards.length;
    };

    const syncHeader = (force = false) => {
      const header = (options.getHeader?.() || '').trim();
      if (!force && header === lastHeader) return cards.length;
      lastHeader = header;
      return render(header);
    };

    const move = direction => {
      const distance = step();
      if (cards.length < 2 || !distance) return;
      let index = current();
      if (direction < 0 && index === 0) {
        jump(cards.length * distance);
        index = cards.length;
      }
      const next = index + direction;
      const generation = renderGeneration;
      track.scrollTo({left: next * distance, behavior: reducedMotion.matches ? 'instant' : 'smooth'});
      if (next === cards.length) wrapTimer = setTimeout(() => {
        wrapTimer = 0;
        if (generation === renderGeneration && track.scrollLeft >= (cards.length - .25) * distance) jump(0);
      }, 750);
    };

    controls.querySelector('[data-cz-hardware-prev]')?.addEventListener('click', () => move(-1));
    controls.querySelector('[data-cz-hardware-next]')?.addEventListener('click', () => move(1));
    pause?.addEventListener('click', () => {
      manualPaused = !manualPaused;
      pause.setAttribute('aria-pressed', String(manualPaused));
      pause.setAttribute('aria-label', manualPaused ? 'Resume hardware carousel' : 'Pause hardware carousel');
      pause.textContent = manualPaused ? '▶' : 'Ⅱ';
    });
    track.addEventListener('scrollend', () => {
      const distance = step();
      if (distance && track.scrollLeft >= (cards.length - .25) * distance) jump(0);
    });
    section.addEventListener('pointerenter', () => { hovered = true; });
    section.addEventListener('pointerleave', () => { hovered = false; });
    section.addEventListener('focusin', () => { focused = true; });
    section.addEventListener('focusout', () => {
      setTimeout(() => { focused = section.contains(document.activeElement); }, 0);
    });
    if (window.IntersectionObserver) {
      new IntersectionObserver(entries => { visible = entries.some(entry => entry.isIntersecting); }, {threshold: 0.3}).observe(section);
    }
    document.addEventListener('change', event => {
      if (!options.headerInputSelector || event.target.closest?.(options.headerInputSelector)) setTimeout(syncHeader, 0);
    });
    if (options.readyEvent) document.addEventListener(options.readyEvent, () => syncHeader(true));
    setInterval(() => {
      if (cards.length > 1 && visible && !document.hidden && !reducedMotion.matches && !manualPaused && !hovered && !focused) move(1);
    }, 5500);

    const controller = {syncHeader, render, get cards() { return [...cards]; }};
    section.czHardwareController = controller;
    section.dataset.czHardwareController = 'shared-v1';
    syncHeader(true);
    return controller;
  }

  window.czDraperyHardware = {init, fitsHeader};
})();
